import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from 'auth/AuthProvider';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { formatMoney } from 'features/company-plans/money';
import { visibleNavGroups } from 'layout/navigation';
import { Card, CardSkeleton, ErrorState, PageHeader } from 'shared/components/Feedback';
import { NavIcon } from 'shared/components/Icons';

export function OverviewPage() {
  const { t, locale } = useI18n();
  const { hasPermission } = useAuth();
  const overviewQuery = useQuery({ queryKey: queryKeys.overview, queryFn: ({ signal }) => assessmentApi.overview({ signal }) });
  const versionsQuery = useQuery({ queryKey: queryKeys.versions, queryFn: ({ signal }) => assessmentApi.listVersions({ signal }) });

  if (overviewQuery.isLoading) {
    return <CardSkeleton count={8} />;
  }
  if (overviewQuery.isError) {
    return <ErrorState title={humanizeApiError(overviewQuery.error, t)} onRetry={overviewQuery.refetch} retryLabel={t('actions.retry')} />;
  }

  const data = overviewQuery.data || {};
  const working = data.working || {};
  const published = data.published_version;
  const latest = data.latest_version;
  const generation = data.generation || {};
  const attempts = data.attempts || {};
  const companyPlans = data.company_plans;
  const pendingZero = working.pending_zero_pattern_ids || [];
  const pendingConfig = Object.values(working.pending_configuration_by_pattern || {}).reduce((sum, count) => sum + Number(count || 0), 0);
  const inactiveQuestions = Number(working.questions_count || 0) - Number(working.active_questions_count || 0);
  const latestBuilt = (versionsQuery.data?.items || [])[0];
  const workingDiffers = latestBuilt && typeof latestBuilt.source_revision === 'number' && latestBuilt.source_revision !== data.revision;
  const generationIncomplete = !['completed'].includes(generation.status);
  const warnings = [
    !published ? t('overview.nonePublished') : null,
    pendingZero.length > 0 ? t('overview.zeroWeights') : null,
    generationIncomplete || !generation.top_three_profiles_count ? t('overview.staleResults') : null,
    workingDiffers ? t('overview.workingDiffers') : null,
    !published || generationIncomplete ? t('overview.incompletePublication') : null,
  ].filter(Boolean);

  const cards = [
    { label: t('overview.activePatterns'), value: working.active_patterns_count },
    { label: t('overview.sections'), value: working.sections_count },
    { label: t('overview.activeQuestions'), value: working.active_questions_count },
    { label: t('overview.inactiveQuestions'), value: inactiveQuestions },
    { label: t('overview.answers'), value: working.answers_count },
    { label: t('overview.pendingWeights'), value: pendingZero.length + pendingConfig },
    { label: t('overview.latestPublished'), value: published ? `#${published.number}` : '—' },
    { label: t('overview.workingRevision'), value: data.revision },
    { label: t('overview.topThreeProfiles'), value: generation.top_three_profiles_count ?? published?.top_three_profiles_count ?? published?.coverage?.found_profiles ?? '—' },
    { label: t('overview.generationStatus'), value: generation.status || '—' },
    { label: t('overview.completedAttempts'), value: attempts.completed },
    { label: t('overview.recentActivity'), value: attempts.completed_last_7_days },
  ];

  if (companyPlans && typeof companyPlans === 'object') {
    cards.push(
      { label: t('overview.activePlans'), value: companyPlans.active_plans },
      { label: t('overview.activeCompanies'), value: companyPlans.active_companies },
      { label: t('overview.pendingPurchases'), value: companyPlans.pending_purchases },
      {
        label: t('overview.paidRevenue'),
        value: companyPlans.paid_revenue_minor != null
          ? formatMoney(companyPlans.paid_revenue_minor, companyPlans.currency || 'SAR', locale)
          : '—',
      },
      { label: t('overview.availableCredits'), value: companyPlans.available_credits },
      { label: t('overview.activeCodes'), value: companyPlans.active_codes },
      { label: t('overview.claimedCodes'), value: companyPlans.claimed_codes },
      { label: t('overview.completedSponsored'), value: companyPlans.completed_sponsored_assessments },
    );
  }

  const shortcuts = visibleNavGroups(hasPermission)
    .flatMap((group) => group.items)
    .filter((item) => item.to !== paths.overview);

  return (
    <div>
      <PageHeader title={t('overview.title')} />
      {warnings.map((warning) => (
        <p key={warning} className="warning-banner" role="status">{warning}</p>
      ))}
      {shortcuts.length > 0 ? (
        <section className="quick-access-grid" aria-label={t('overview.quickAccess')}>
          {shortcuts.map((item) => (
            <Link key={item.to} className="quick-access-card" to={item.to}>
              <span className="quick-access-icon" aria-hidden="true">
                <NavIcon name={item.icon} className="quick-access-svg" />
              </span>
              <strong>{t(item.key)}</strong>
            </Link>
          ))}
        </section>
      ) : null}
      <div className="stat-grid">
        {cards.map((card) => (
          <Card key={card.label}>
            <p className="muted">{card.label}</p>
            <strong className="stat">{card.value ?? '—'}</strong>
          </Card>
        ))}
      </div>
      <p className="muted">{t('overview.noRecentActivity')}</p>
      {latest ? (
        <p>
          <Link to={paths.versionDetail(latest.id)}>{t('nav.versions')} #{latest.number}</Link>
        </p>
      ) : null}
    </div>
  );
}
